import React, { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import EditIcon from '@material-ui/icons/Edit'
import DeleteIcon from '@material-ui/icons/Delete'
import RefreshIcon from '@material-ui/icons/Refresh'
import SearchIcon from '@material-ui/icons/Search'

import { axiosInstance } from '../../../helpers'
import { useOrganization } from '../../../hooks/useOrganization'
import { Selector } from '../../../components/Selector/Selector'

// ─── Types ────────────────────────────────────────────────────────────────────

interface BannerNotification {
    id: number
    title: string
    message: string
    start_date: string
    expiration_date: string
    is_active: boolean
    organization: number | null
    organization_name: string | null
    created_by: number
    created_by_name: string
    created_date: string
    modified_date: string
}

interface BannerFormState {
    title: string
    message: string
    start_date: string
    expiration_date: string
    is_active: boolean
    organization: number | null
}

type SortKey =
    | 'title'
    | 'organization_name'
    | 'start_date'
    | 'expiration_date'
    | 'status'
    | 'created_by_name'
type SortDir = 'asc' | 'desc'

const EMPTY_FORM: BannerFormState = {
    title: '',
    message: '',
    start_date: '',
    expiration_date: '',
    is_active: true,
    organization: null,
}

const toLocalDateValue = (iso: string) => (iso ? iso.slice(0, 10) : '')

const isExpired = (expiration_date: string) =>
    new Date(expiration_date) < new Date()

// ─── Form dialog ──────────────────────────────────────────────────────────────

interface FormDialogProps {
    open: boolean
    editing: BannerNotification | null
    organizationList: { id: number; name: string }[]
    onClose: () => void
    onSaved: () => void
}

const EVERYONE_SENTINEL = -1

const FormDialog = ({
    open,
    editing,
    organizationList,
    onClose,
    onSaved,
}: FormDialogProps) => {
    const [form, setForm] = useState<BannerFormState>(EMPTY_FORM)
    const [saving, setSaving] = useState(false)
    const theme = useTheme()

    useEffect(() => {
        if (open) {
            setForm(
                editing
                    ? {
                          title: editing.title,
                          message: editing.message,
                          start_date: toLocalDateValue(editing.start_date),
                          expiration_date: toLocalDateValue(
                              editing.expiration_date,
                          ),
                          is_active: editing.is_active,
                          organization: editing.organization,
                      }
                    : EMPTY_FORM,
            )
        }
    }, [open, editing])

    const set = (field: keyof BannerFormState) => (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => setForm((prev) => ({ ...prev, [field]: e.target.value }))

    const handleSave = async () => {
        if (
            !form.title ||
            !form.message ||
            !form.start_date ||
            !form.expiration_date
        )
            return
        setSaving(true)
        const body = {
            ...form,
            start_date: `${form.start_date}T00:00:00Z`,
            expiration_date: `${form.expiration_date}T23:59:59Z`,
        }
        try {
            if (editing) {
                await axiosInstance.patch(
                    `account-manager/banner-notifications/${editing.id}/`,
                    body,
                )
                toast.success('Banner updated')
            } else {
                await axiosInstance.post(
                    'account-manager/banner-notifications/',
                    body,
                )
                toast.success('Banner created')
            }
            onSaved()
            onClose()
        } catch (e) {
            toast.error(`Failed to save: ${e}`)
        } finally {
            setSaving(false)
        }
    }

    const selectorData = [
        { id: EVERYONE_SENTINEL, name: 'Everyone' },
        ...organizationList,
    ]

    const valid =
        form.title.trim() &&
        form.message.trim() &&
        form.start_date &&
        form.expiration_date

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{editing ? 'Edit Banner' : 'New Banner'}</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    fullWidth
                    label="Title"
                    variant="outlined"
                    size="small"
                    value={form.title}
                    onChange={set('title')}
                    inputProps={{ maxLength: 255 }}
                    style={{ marginBottom: theme.spacing(2) }}
                />
                <TextField
                    fullWidth
                    label="Message"
                    variant="outlined"
                    size="small"
                    multiline
                    rows={3}
                    value={form.message}
                    onChange={set('message')}
                    style={{ marginBottom: theme.spacing(2) }}
                />
                <div
                    style={{
                        display: 'flex',
                        gap: theme.spacing(2),
                        marginBottom: theme.spacing(2),
                    }}
                >
                    <TextField
                        fullWidth
                        label="Start date"
                        type="date"
                        variant="outlined"
                        size="small"
                        value={form.start_date}
                        onChange={set('start_date')}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        fullWidth
                        label="Expiration date"
                        type="date"
                        variant="outlined"
                        size="small"
                        value={form.expiration_date}
                        onChange={set('expiration_date')}
                        InputLabelProps={{ shrink: true }}
                    />
                </div>
                <div style={{ marginBottom: theme.spacing(2) }}>
                    <Typography
                        variant="caption"
                        color="textSecondary"
                        style={{ display: 'block', marginBottom: 4 }}
                    >
                        Organization
                    </Typography>
                    <Selector
                        data={selectorData}
                        currentValue={form.organization ?? EVERYONE_SENTINEL}
                        getDisplayString={(o) => o.name}
                        onChange={(e) => {
                            const val = Number(e.target.value)
                            setForm((prev) => ({
                                ...prev,
                                organization:
                                    val === EVERYONE_SENTINEL ? null : val,
                            }))
                        }}
                        searchable
                        customStyle={{ formControl: { width: '100%' } }}
                        popupHeight={300}
                    />
                </div>
                <FormControlLabel
                    control={
                        <Switch
                            checked={form.is_active}
                            onChange={(e) =>
                                setForm((prev) => ({
                                    ...prev,
                                    is_active: e.target.checked,
                                }))
                            }
                            color="primary"
                        />
                    }
                    label="Active"
                />
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    size="small"
                    disabled={saving}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                    size="small"
                    disabled={!valid || saving}
                >
                    {saving ? (
                        <CircularProgress size={16} />
                    ) : editing ? (
                        'Save'
                    ) : (
                        'Create'
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

// ─── Main page ────────────────────────────────────────────────────────────────

const statusWeight = (b: BannerNotification) => {
    if (isExpired(b.expiration_date)) return 2
    if (!b.is_active) return 1
    return 0
}

export const BannerNotificationsPage = () => {
    const theme = useTheme()
    const [banners, setBanners] = useState<BannerNotification[]>([])
    const [loading, setLoading] = useState(false)
    const [search, setSearch] = useState('')
    const [sortKey, setSortKey] = useState<SortKey>('expiration_date')
    const [sortDir, setSortDir] = useState<SortDir>('asc')
    const [formOpen, setFormOpen] = useState(false)
    const [editing, setEditing] = useState<BannerNotification | null>(null)
    const [
        deleteConfirm,
        setDeleteConfirm,
    ] = useState<BannerNotification | null>(null)
    const [deleting, setDeleting] = useState(false)

    const { organizationList, getOrganizationList } = useOrganization()

    useEffect(() => {
        fetchBanners()
        getOrganizationList({})
    }, [])

    const fetchBanners = async () => {
        setLoading(true)
        try {
            const res = await axiosInstance.get(
                'account-manager/banner-notifications/',
            )
            setBanners(res.data)
        } catch {
            toast.error('Failed to load banners')
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = async () => {
        if (!deleteConfirm) return
        setDeleting(true)
        try {
            await axiosInstance.delete(
                `account-manager/banner-notifications/${deleteConfirm.id}/`,
            )
            toast.success('Banner deleted')
            setBanners((prev) => prev.filter((b) => b.id !== deleteConfirm.id))
            setDeleteConfirm(null)
        } catch {
            toast.error('Failed to delete banner')
        } finally {
            setDeleting(false)
        }
    }

    const handleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
        } else {
            setSortKey(key)
            setSortDir('asc')
        }
    }

    const displayedBanners = useMemo(() => {
        const q = search.toLowerCase()
        const filtered = banners.filter(
            (b) =>
                !q ||
                b.title.toLowerCase().includes(q) ||
                b.message.toLowerCase().includes(q) ||
                (b.organization_name ?? 'Everyone').toLowerCase().includes(q) ||
                b.created_by_name.toLowerCase().includes(q),
        )

        return [...filtered].sort((a, b) => {
            let cmp = 0
            switch (sortKey) {
                case 'title':
                    cmp = a.title.localeCompare(b.title)
                    break
                case 'organization_name':
                    cmp = (a.organization_name ?? 'Everyone').localeCompare(
                        b.organization_name ?? 'Everyone',
                    )
                    break
                case 'start_date':
                    cmp =
                        new Date(a.start_date).getTime() -
                        new Date(b.start_date).getTime()
                    break
                case 'expiration_date':
                    cmp =
                        new Date(a.expiration_date).getTime() -
                        new Date(b.expiration_date).getTime()
                    break
                case 'status':
                    cmp = statusWeight(a) - statusWeight(b)
                    break
                case 'created_by_name':
                    cmp = a.created_by_name.localeCompare(b.created_by_name)
                    break
            }
            return sortDir === 'asc' ? cmp : -cmp
        })
    }, [banners, search, sortKey, sortDir])

    const SortHeader = ({
        label,
        id,
        width,
    }: {
        label: string
        id: SortKey
        width?: number
    }) => (
        <TableCell style={{ fontWeight: 700, width }}>
            <TableSortLabel
                active={sortKey === id}
                direction={sortKey === id ? sortDir : 'asc'}
                onClick={() => handleSort(id)}
            >
                {label}
            </TableSortLabel>
        </TableCell>
    )

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                padding: theme.spacing(2),
                gap: theme.spacing(2),
            }}
        >
            {/* Header */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: theme.spacing(1),
                }}
            >
                <Typography variant="h6" style={{ fontWeight: 700 }}>
                    Banner Notifications
                </Typography>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: theme.spacing(1),
                    }}
                >
                    <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Search..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ width: 220 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Tooltip title="Refresh">
                        <IconButton
                            size="small"
                            onClick={fetchBanners}
                            disabled={loading}
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            setEditing(null)
                            setFormOpen(true)
                        }}
                    >
                        New Banner
                    </Button>
                </div>
            </div>

            {/* Table */}
            <TableContainer
                component={Paper}
                variant="outlined"
                style={{ flex: 1, overflow: 'auto' }}
            >
                {loading ? (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            padding: theme.spacing(6),
                        }}
                    >
                        <CircularProgress />
                    </div>
                ) : (
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <SortHeader
                                    label="Title"
                                    id="title"
                                    width={220}
                                />
                                <TableCell
                                    style={{ fontWeight: 700, width: '100%' }}
                                >
                                    Message
                                </TableCell>
                                <SortHeader
                                    label="Organization"
                                    id="organization_name"
                                    width={190}
                                />
                                <SortHeader
                                    label="Starts"
                                    id="start_date"
                                    width={130}
                                />
                                <SortHeader
                                    label="Expires"
                                    id="expiration_date"
                                    width={130}
                                />
                                <SortHeader
                                    label="Status"
                                    id="status"
                                    width={120}
                                />
                                <SortHeader
                                    label="Created by"
                                    id="created_by_name"
                                    width={180}
                                />
                                <TableCell width={90} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {displayedBanners.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={7}
                                        align="center"
                                        style={{
                                            padding: theme.spacing(4),
                                            color: theme.palette.text.secondary,
                                        }}
                                    >
                                        {search
                                            ? 'No banners match your search'
                                            : 'No banners found'}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                displayedBanners.map((banner) => {
                                    const expired = isExpired(
                                        banner.expiration_date,
                                    )
                                    return (
                                        <TableRow key={banner.id} hover>
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    style={{ fontWeight: 600 }}
                                                >
                                                    {banner.title}
                                                </Typography>
                                            </TableCell>
                                            <TableCell
                                                style={{
                                                    width: '100%',
                                                    maxWidth: 0,
                                                }}
                                            >
                                                <Tooltip
                                                    title={banner.message}
                                                    placement="top"
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        style={{
                                                            overflow: 'hidden',
                                                            textOverflow:
                                                                'ellipsis',
                                                            whiteSpace:
                                                                'nowrap',
                                                            cursor: 'default',
                                                        }}
                                                    >
                                                        {banner.message}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {banner.organization_name ??
                                                        'Everyone'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {new Date(
                                                        banner.start_date,
                                                    ).toLocaleDateString(
                                                        undefined,
                                                        { timeZone: 'UTC' },
                                                    )}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    style={{
                                                        color: expired
                                                            ? theme.palette
                                                                  .error.main
                                                            : undefined,
                                                    }}
                                                >
                                                    {new Date(
                                                        banner.expiration_date,
                                                    ).toLocaleDateString(
                                                        undefined,
                                                        { timeZone: 'UTC' },
                                                    )}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {expired ? (
                                                    <Chip
                                                        label="Expired"
                                                        size="small"
                                                        style={{
                                                            backgroundColor:
                                                                theme.palette
                                                                    .grey[300],
                                                            fontWeight: 700,
                                                            fontSize: 11,
                                                        }}
                                                    />
                                                ) : banner.is_active ? (
                                                    <Chip
                                                        label="Active"
                                                        size="small"
                                                        style={{
                                                            backgroundColor:
                                                                '#e8f5e9',
                                                            color: '#2e7d32',
                                                            fontWeight: 700,
                                                            fontSize: 11,
                                                        }}
                                                    />
                                                ) : (
                                                    <Chip
                                                        label="Inactive"
                                                        size="small"
                                                        style={{
                                                            backgroundColor:
                                                                '#fff3e0',
                                                            color: '#e65100',
                                                            fontWeight: 700,
                                                            fontSize: 11,
                                                        }}
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                >
                                                    {banner.created_by_name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Tooltip title="Edit">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            setEditing(banner)
                                                            setFormOpen(true)
                                                        }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            setDeleteConfirm(
                                                                banner,
                                                            )
                                                        }
                                                        style={{
                                                            marginLeft: 4,
                                                        }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    )
                                })
                            )}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>

            {/* Create / Edit dialog */}
            <FormDialog
                open={formOpen}
                editing={editing}
                organizationList={organizationList}
                onClose={() => setFormOpen(false)}
                onSaved={fetchBanners}
            />

            {/* Delete confirmation */}
            <Dialog
                open={!!deleteConfirm}
                onClose={() => setDeleteConfirm(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Delete Banner</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Are you sure you want to delete{' '}
                        <strong>&ldquo;{deleteConfirm?.title}&rdquo;</strong>?
                        This cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setDeleteConfirm(null)}
                        variant="outlined"
                        size="small"
                        disabled={deleting}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDelete}
                        variant="contained"
                        color="secondary"
                        size="small"
                        disabled={deleting}
                    >
                        {deleting ? <CircularProgress size={16} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}
